/* eslint-disable no-console, no-undef */
import React, { useEffect, useState, useCallback } from "react";
import { Tab, Loader } from "semantic-ui-react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { User } from "../../../api";
import { useAuth } from "../../../hooks";
import {
  PersonalInfoTab,
  SecurityTab,
  AcademicInfoTab,
} from "../../../components/Student/MyProfile";
import "./MyProfile.scss";

const userController = new User();

export function MyProfile() {
  const { accessToken, setUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState(null);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });
  const [formMessage, setFormMessage] = useState({
    show: false,
    type: "",
    content: "",
  });

  // Formik para información personal
  const formikPersonalInfo = useFormik({
    initialValues: {
      phone: "",
      phone2: "",
      address: "",
      email: "",
      fileAvatar: null,
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      phone: Yup.string(),
      phone2: Yup.string(),
      address: Yup.string(),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const updatedUserData = {
          phone: values.phone,
          phone2: values.phone2,
          address: values.address,
        };

        if (values.fileAvatar) {
          updatedUserData.fileAvatar = values.fileAvatar;
        }

        const response = await userController.updateUser(
          userData.id,
          updatedUserData
        );

        setFormMessage({
          show: true,
          type: "success",
          content: "Información actualizada con éxito",
        });

        setTimeout(() => {
          setFormMessage({ show: false, type: "", content: "" });
        }, 3000);

        setUserData(response.user);
        formikPersonalInfo.setValues({
          phone: response.user.phone || "",
          phone2: response.user.phone2 || "",
          address: response.user.address || "",
          email: response.user.email || "",
          fileAvatar: null,
        });

        if (setUser) {
          setUser(response.user);
        }

        setIsEditing(false);
      } catch (error) {
        setFormMessage({
          show: true,
          type: "error",
          content:
            error.msg || error.message || "Error al actualizar la información",
        });

        setTimeout(() => {
          setFormMessage({ show: false, type: "", content: "" });
        }, 5000);
      } finally {
        setLoading(false);
      }
    },
  });

  // Formik para contraseña
  const formikPassword = useFormik({
    initialValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      currentPassword: Yup.string().required(
        "La contraseña actual es obligatoria"
      ),
      newPassword: Yup.string()
        .min(8, "La contraseña debe tener al menos 8 caracteres")
        .required("La nueva contraseña es obligatoria")
        .test(
          "different-from-current",
          "La nueva contraseña debe ser diferente a la actual",
          function (value) {
            return value !== this.parent.currentPassword;
          }
        ),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("newPassword"), null], "Las contraseñas no coinciden")
        .required("Debes confirmar la nueva contraseña"),
    }),
    onSubmit: async (values, { resetForm }) => {
      setLoading(true);
      try {
        await userController.changePassword(
          values.currentPassword,
          values.newPassword
        );

        setFormMessage({
          show: true,
          type: "success",
          content: "Contraseña actualizada correctamente",
        });

        resetForm();

        setTimeout(() => {
          setFormMessage({ show: false, type: "", content: "" });
        }, 3000);
      } catch (error) {
        setFormMessage({
          show: true,
          type: "error",
          content:
            error.msg || error.message || "Error al actualizar la contraseña",
        });

        setTimeout(() => {
          setFormMessage({ show: false, type: "", content: "" });
        }, 5000);
      } finally {
        setLoading(false);
      }
    },
  });

  const fetchUserData = useCallback(async () => {
    try {
      setLoadingInitial(true);
      const response = await userController.getMe();
      setUserData(response);

      formikPersonalInfo.setValues({
        phone: response.phone || "",
        phone2: response.phone2 || "",
        address: response.address || "",
        email: response.email || "",
      });

      if (setUser) {
        setUser(response);
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error al cargar datos",
        text: "No se pudieron cargar tus datos personales. Por favor, intenta de nuevo más tarde.",
        confirmButtonText: "Entendido",
        confirmButtonColor: "#db2828",
      });
      console.error("Error fetching user data:", error);
    } finally {
      setLoadingInitial(false);
    }
  }, [accessToken, setUser]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  const togglePasswordVisibility = useCallback((field) => {
    setShowPasswords((prevState) => ({
      ...prevState,
      [field]: !prevState[field],
    }));
  }, []);

  const clearFormMessage = useCallback(() => {
    setFormMessage({ show: false, type: "", content: "" });
  }, []);

  const panes = React.useMemo(
    () => [
      {
        menuItem: {
          key: "info",
          icon: "user",
          content: "Información Personal",
        },
        render: () => (
          <Tab.Pane attached={false} className="my-profile__tab-pane">
            <PersonalInfoTab
              userData={userData}
              loading={loading}
              formMessage={formMessage}
              formik={formikPersonalInfo}
              isEditing={isEditing}
              setIsEditing={setIsEditing}
            />
          </Tab.Pane>
        ),
      },
      {
        menuItem: { key: "security", icon: "lock", content: "Cambiar contraseña" },
        render: () => (
          <Tab.Pane attached={false} className="my-profile__tab-pane">
            <SecurityTab
              loading={loading}
              formMessage={formMessage}
              formik={formikPassword}
              showPasswords={showPasswords}
              togglePasswordVisibility={togglePasswordVisibility}
              clearFormMessage={clearFormMessage}
            />
          </Tab.Pane>
        ),
      },
      {
        menuItem: {
          key: "account",
          icon: "university",
          content: "Información Académica",
        },
        render: () => (
          <Tab.Pane attached={false} className="my-profile__tab-pane">
            <AcademicInfoTab userData={userData} />
          </Tab.Pane>
        ),
      },
    ],
    [
      isEditing,
      loading,
      formMessage,
      userData,
      formikPersonalInfo,
      formikPassword,
      showPasswords,
      togglePasswordVisibility,
      clearFormMessage,
    ]
  );

  if (loadingInitial) {
    return (
      <div className="my-profile__loading">
        <Loader active inline="centered" size="large" />
        <p>Cargando información del perfil...</p>
      </div>
    );
  }

  return (
    <div className="my-profile">
      <h1 className="my-profile__title">Mi Perfil</h1>
      <p className="my-profile__description">
        Gestiona tu información personal y configuración de cuenta.
      </p>

      <Tab
        menu={{ secondary: true, pointing: true }}
        panes={panes}
        className="my-profile__tabs"
      />
    </div>
  );
}
